import * as React from 'react';
import { useState } from 'react';
import { Box, Grid, TextField, Button, MenuItem } from '@mui/material';
import type { InvoiceSearchV2Filters } from '../Types/invoiceSearchV2Types';
import { defaultInvoiceSearchV2Filters } from '../Types/invoiceSearchV2Types';
import type { InvoiceStatus, InvoiceType } from '../Types/invoicesTypes';
import { InvoiceStatusOptions, InvoiceTypeOptions } from '../Types/invoicesLabels';

interface InvoicesSearchFormProps {
  onSearch: (filters: InvoiceSearchV2Filters) => void;
}

const isInvoiceType = (value: string): value is InvoiceType =>
  InvoiceTypeOptions.some((option) => option.value === value);

const isInvoiceStatus = (value: string): value is InvoiceStatus =>
  InvoiceStatusOptions.some((option) => option.value === value);

const InvoicesSearchForm: React.FC<InvoicesSearchFormProps> = ({ onSearch }) => {
  const [filters, setFilters] = useState<InvoiceSearchV2Filters>(defaultInvoiceSearchV2Filters);

  const patch = (p: Partial<InvoiceSearchV2Filters>) =>
    setFilters((prev) => ({ ...prev, ...p }));

  const handleReset = () => {
    setFilters(defaultInvoiceSearchV2Filters);
    onSearch(defaultInvoiceSearchV2Filters);
  };

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        onSearch(filters);
      }}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            label="Szukaj (fulltext)"
            value={filters.searchTerm}
            onChange={(e) => patch({ searchTerm: e.target.value })}
            placeholder="Klient, oferta, e-mail…"
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            label="Numer faktury"
            value={filters.invoiceNumber}
            onChange={(e) => patch({ invoiceNumber: e.target.value })}
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            label="Nabywca"
            value={filters.buyerName}
            onChange={(e) => patch({ buyerName: e.target.value })}
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            label="Miasto nabywcy"
            value={filters.buyerAddress}
            onChange={(e) => patch({ buyerAddress: e.target.value })}
            placeholder="np. Bytom, Warszawa…"
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            label="Odbiorca"
            value={filters.recipientName}
            onChange={(e) => patch({ recipientName: e.target.value })}
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <TextField
            select
            label="Typ"
            value={filters.type}
            onChange={(e) => patch({ type: isInvoiceType(e.target.value) ? e.target.value : '' })}
            fullWidth
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {InvoiceTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <TextField
            select
            label="Status"
            value={filters.status}
            onChange={(e) => patch({ status: isInvoiceStatus(e.target.value) ? e.target.value : '' })}
            fullWidth
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {InvoiceStatusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <TextField
            type="date"
            label="Data wystawienia od"
            value={filters.issueDateFrom}
            onChange={(e) => patch({ issueDateFrom: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <TextField
            type="date"
            label="Data wystawienia do"
            value={filters.issueDateTo}
            onChange={(e) => patch({ issueDateTo: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <TextField
            type="number"
            label="Kwota od"
            value={filters.totalAmountFrom}
            onChange={(e) => patch({ totalAmountFrom: e.target.value })}
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <TextField
            type="number"
            label="Kwota do"
            value={filters.totalAmountTo}
            onChange={(e) => patch({ totalAmountTo: e.target.value })}
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Box display="flex" gap={1} justifyContent="flex-end">
            <Button type="button" variant="text" onClick={handleReset}>
              Wyczyść
            </Button>
            <Button type="submit" variant="contained">
              Szukaj
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InvoicesSearchForm;
